import { useCallback, useState } from "react";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";
import { useBitgetWsConnection } from "@/hooks/exchanges/direct-ws/bitget";
import { formatBitgetInstId, getBitgetInstType } from "@/hooks/exchanges/direct-ws/bitget/utils";

const EXCHANGE_ID = "bitget";

interface BitgetOrderBookMessage {
  action?: string;
  arg?: {
    instType: string;
    channel: string;
    instId: string;
  };
  data?: Array<{
    bids?: [string, string][];
    asks?: [string, string][];
    ts?: string;
    checksum?: number;
    seq?: number;
  }>;
}

/**
 * Hook for subscribing to Bitget orderbook updates via shared WebSocket.
 * Uses the `books15` channel which pushes full snapshots (15 depth levels) every ~150ms.
 */
export const useBitgetOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [orderBookError, setOrderBookError] = useState<string | null>(null);

  const setup = useCallback(
    (ws: WebSocket) => {
      if (!pairSymbol) return;

      // Clear stale data from previous pair before subscribing to the new one
      setOrderBookData(null);

      const instId = formatBitgetInstId(pairSymbol);
      const instType = getBitgetInstType(pairSymbol);
      const subscribeArg = { instType, channel: "books15", instId };

      ws.send(JSON.stringify({ op: "subscribe", args: [subscribeArg] }));

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ op: "unsubscribe", args: [subscribeArg] }));
        }
      };
    },
    [pairSymbol],
  );

  const onMessage = useCallback(
    (msg: unknown) => {
      if (!pairSymbol) return;

      try {
        const typed = msg as BitgetOrderBookMessage;

        // Only process depth data pushes (action: "snapshot" for books15)
        if (!typed.action || !typed.data?.length) return;

        const expectedInstId = formatBitgetInstId(pairSymbol);
        if (typed.arg?.channel !== "books15" || typed.arg?.instId !== expectedInstId) return;

        const bookData = typed.data[0];
        const rawBids = bookData?.bids;
        const rawAsks = bookData?.asks;

        if (rawBids || rawAsks) {
          const bids: [number, number][] = (rawBids || []).map(([price, size]) => [
            parseFloat(price),
            parseFloat(size),
          ]);

          const asks: [number, number][] = (rawAsks || []).map(([price, size]) => [
            parseFloat(price),
            parseFloat(size),
          ]);

          setOrderBookData({
            timestamp: bookData?.ts ? parseInt(bookData.ts) : Date.now(),
            exchangeId: EXCHANGE_ID,
            pairSymbol,
            bids,
            asks,
          });
          setOrderBookError(null);
        }
      } catch {
        setOrderBookError("Failed to parse orderbook update");
      }
    },
    [pairSymbol],
  );

  const { isConnected, error: connectionError } = useBitgetWsConnection({
    setup,
    onMessage,
    enabled: enabled && !!pairSymbol,
  });

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error: orderBookError ?? connectionError,
  };
};
